import { useEffect, useMemo } from "react";

import { useThemeMode } from "@internal/backstage-plugin-utility-react/hooks";

import {
    ALL_SCHEMES,
    CODE_FONTS,
    FONT_CHANGE_EVENT,
    FONT_DEFAULT,
    FONT_FACE_CSS,
    FONT_SIZE_CHANGE_EVENT,
    FONT_SIZE_DEFAULT,
    FONT_SIZE_STORAGE_KEY,
    FONT_STORAGE_KEY,
    parseSize,
} from "../../constants";
import { useCodeSchemeName } from "../../hooks/useCodeSchemes";
import { usePersistedSetting } from "../../hooks/usePersistedSetting";
import { useShadowStyle } from "../../hooks/useShadowStyle";

// TODO: replace JSON schemes with static .css files and class-scoped selectors,
// injected once via adoptedStyleSheets and switched by toggling classNames.
/**
 * Global CSS tweaks applied inside the TechDocs shadow DOM to fix MkDocs
 * tabbed-label font sizes that would otherwise be overridden by the host page.
 */
const GLOBAL_TWEAKS = `.md-typeset .tabbed-labels > label { font-size: 1rem !important; }`;

/**
 * Guards against injecting `@font-face` declarations into `document.head`
 * more than once across the lifetime of the page.
 */
let fontFacesInjected = false;

/**
 * Headless TechDocs addon that applies syntax-highlight theme colors, code
 * font family, and code text size to the TechDocs shadow DOM.
 *
 * The component subscribes to the active Backstage app theme and reads
 * persisted user settings for scheme, font, and text size. It computes the
 * corresponding CSS and injects it into the shadow root via
 * {@link useShadowStyle}. It also performs a one-time injection of
 * `@font-face` declarations into `document.head`, which browsers require to
 * be outside the shadow DOM.
 *
 * Renders nothing -- returns `null`.
 *
 * @public
 */
export const CodeHighlightTheme = () => {
    const { isDark } = useThemeMode();

    const [schemeName] = useCodeSchemeName();

    const [fontLabel] = usePersistedSetting(
        FONT_STORAGE_KEY,
        FONT_DEFAULT,
        FONT_CHANGE_EVENT
    );

    const [rawSize] = usePersistedSetting(
        FONT_SIZE_STORAGE_KEY,
        String(FONT_SIZE_DEFAULT),
        FONT_SIZE_CHANGE_EVENT
    );

    const schemeCss = useMemo(() => {
        const scheme =
            ALL_SCHEMES.find(s => s.name === schemeName) ?? ALL_SCHEMES[0];
        const themeContent = isDark ? scheme.dark : scheme.light;
        return `${themeContent || ""}\n${GLOBAL_TWEAKS}`;
    }, [isDark, schemeName]);

    const fontCss = useMemo(() => {
        const font = CODE_FONTS.find(f => f.label === fontLabel);
        return font?.value
            ? `pre > code, .highlight pre { font-family: ${font.value} !important; }`
            : "";
    }, [fontLabel]);

    const sizeCss = useMemo(() => {
        const size = parseSize(rawSize);
        const htmlFontSize =
            parseFloat(getComputedStyle(document.documentElement).fontSize) ||
            16;
        const px = htmlFontSize * (size / 100);
        return `pre > code, .highlight pre { font-size: ${px}px !important; }`;
    }, [rawSize]);

    // @font-face must be in the document scope -- browsers ignore it inside Shadow DOM.
    useEffect(() => {
        if (fontFacesInjected) return;
        fontFacesInjected = true;

        const style = document.createElement("style");
        style.setAttribute("data-addon", "code-font-faces");
        style.textContent = FONT_FACE_CSS;
        document.head.appendChild(style);
    }, []);

    useShadowStyle("code-highlight-theme", schemeCss);
    useShadowStyle("code-font-family", fontCss);
    useShadowStyle("code-text-size", sizeCss);

    return null;
};
